/**
 * WHAT: SQL template management for the Custom SQL Executor module.
 * WHY: Provides a governed workflow for creating, validating, and approving SQL
 *      templates that organizations use to extract user access data from databases,
 *      enforcing security rules that block DDL/DML operations.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_CONFIG from '../config/api.config';

/**
 * WHAT: SQL template CRUD with integrated SQL editor, validation, and approval workflow.
 * WHY: Organizations must define and approve SQL queries before execution against
 *      production databases, ensuring security validation blocks destructive operations
 *      while enabling legitimate UAR data extraction.
 */
const SqlTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // create, edit, view
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [filters, setFilters] = useState({
    technology: '',
    status: '',
    approval: '',
  });

  // Form state
  const [formData, setFormData] = useState({
    template_code: '',
    template_name: '',
    description: '',
    technology: 'ORACLE',
    sql_statement: '',
    placeholders: [],
    column_mappings: [],
    output_type: 'RAW',
    approval_required: true,
  });
  const [validationResult, setValidationResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const technologies = ['ORACLE', 'MSSQL', 'POSTGRESQL', 'MYSQL', 'CASSANDRA'];
  const outputTypes = [
    { value: 'RAW', label: 'Raw Data' },
    { value: 'UAR_DATABASE', label: 'UAR Database Accounts' },
    { value: 'UAR_ACCOUNTS', label: 'UAR General Accounts' },
    { value: 'UAR_ENTITLEMENTS', label: 'UAR Entitlements' },
    { value: 'CUSTOM', label: 'Custom Mapping' },
  ];

  /* WHAT: Loads the template list from the API, applying technology/status/approval filters.
   * WHY: The table must always reflect the current filter state; wrapped in useCallback
   *      so it can be a stable dependency for useEffect. */
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.technology) params.append('technology', filters.technology);
      if (filters.status) params.append('status', filters.status);
      if (filters.approval) params.append('approval', filters.approval);

      const response = await axios.get(
        `${API_CONFIG.BASE_URL}/api/custom/templates/?${params.toString()}`
      );
      setTemplates(response.data.templates || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  /* WHAT: Resets the form to blank defaults and opens the modal in create mode.
   * WHY: Each new template starts with a clean form to avoid carry-over from a previous edit. */
  const handleCreateNew = () => {
    setFormData({
      template_code: '',
      template_name: '',
      description: '',
      technology: 'ORACLE',
      sql_statement: '',
      placeholders: [],
      column_mappings: [],
      output_type: 'RAW',
      approval_required: true,
    });
    setValidationResult(null);
    setModalMode('create');
    setShowModal(true);
  };

  /* WHAT: Fetches full template details (including SQL body) and opens the modal in edit mode.
   * WHY: The list view omits the sql_statement for performance; editing requires the full payload. */
  const handleEdit = async (template) => {
    try {
      // Fetch full template details including sql_statement
      const response = await axios.get(
        `${API_CONFIG.BASE_URL}/api/custom/templates/${template.template_id}/`
      );
      const fullTemplate = response.data;
      setSelectedTemplate(fullTemplate);
      setFormData({
        template_code: fullTemplate.template_code,
        template_name: fullTemplate.template_name,
        description: fullTemplate.description || '',
        technology: fullTemplate.technology,
        sql_statement: fullTemplate.sql_statement || '',
        placeholders: fullTemplate.placeholders || [],
        column_mappings: fullTemplate.column_mappings || [],
        output_type: fullTemplate.output_type,
        approval_required: fullTemplate.approval_required,
      });
      setValidationResult(null);
      setModalMode('edit');
      setShowModal(true);
    } catch (err) {
      console.error('Error fetching template details:', err);
      alert('Failed to load template details for editing');
    }
  };

  /* WHAT: Fetches full template details and opens the modal in read-only view mode.
   * WHY: View mode lets users inspect a template's SQL and metadata without risk of accidental edits. */
  const handleView = async (template) => {
    try {
      const response = await axios.get(
        `${API_CONFIG.BASE_URL}/api/custom/templates/${template.template_id}/`
      );
      setSelectedTemplate(response.data);
      setFormData({
        template_code: response.data.template_code,
        template_name: response.data.template_name,
        description: response.data.description || '',
        technology: response.data.technology,
        sql_statement: response.data.sql_statement || '',
        placeholders: response.data.placeholders || [],
        column_mappings: response.data.column_mappings || [],
        output_type: response.data.output_type,
        approval_required: response.data.approval_required,
      });
      setModalMode('view');
      setShowModal(true);
    } catch (err) {
      console.error('Error fetching template details:', err);
      alert('Failed to load template details');
    }
  };

  /* WHAT: Creates or updates a template via POST/PUT based on the current modal mode.
   * WHY: A single save handler simplifies the modal by branching on create vs. edit internally. */
  const handleSave = async () => {
    setSaving(true);
    try {
      if (modalMode === 'create') {
        await axios.post(`${API_CONFIG.BASE_URL}/api/custom/templates/`, formData);
      } else {
        await axios.put(
          `${API_CONFIG.BASE_URL}/api/custom/templates/${selectedTemplate.template_id}/`,
          formData
        );
      }
      setShowModal(false);
      fetchTemplates();
    } catch (err) {
      console.error('Error saving template:', err);
      alert(err.response?.data?.error || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  /* WHAT: Sends the saved template to the server for SQL security validation.
   * WHY: Templates must pass DDL/DML blocking rules before they can be approved for execution. */
  const handleValidate = async () => {
    if (!selectedTemplate) return;
    try {
      const response = await axios.post(
        `${API_CONFIG.BASE_URL}/api/custom/templates/${selectedTemplate.template_id}/validate/`
      );
      setValidationResult(response.data);
    } catch (err) {
      console.error('Error validating template:', err);
      alert('Failed to validate template');
    }
  };

  /* WHAT: Approves or rejects a pending template via the approval endpoint.
   * WHY: The governed workflow requires an explicit approval step before a template
   *      can be executed against production databases. */
  const handleApprove = async (templateId, action) => {
    try {
      await axios.post(
        `${API_CONFIG.BASE_URL}/api/custom/templates/${templateId}/approve/`,
        { action, notes: '' }
      );
      fetchTemplates();
    } catch (err) {
      console.error('Error approving template:', err);
      alert('Failed to update approval status');
    }
  };

  /* WHAT: Soft-deletes (deactivates) a template after user confirmation.
   * WHY: Templates are deactivated rather than hard-deleted to preserve audit history. */
  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to deactivate this template?')) return;
    try {
      await axios.delete(`${API_CONFIG.BASE_URL}/api/custom/templates/${templateId}/`);
      fetchTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      alert('Failed to delete template');
    }
  };

  /* WHAT: Maps a validation status to a Bootstrap badge color class.
   * WHY: Color-coding (green=VALID, red=INVALID) provides instant visual feedback in the table. */
  const getStatusBadge = (status) => {
    const badges = {
      VALID: 'bg-success',
      INVALID: 'bg-danger',
      PENDING: 'bg-warning text-dark',
      SKIPPED: 'bg-secondary',
    };
    return badges[status] || 'bg-secondary';
  };

  /* WHAT: Maps an approval status to a Bootstrap badge color class.
   * WHY: Distinguishes APPROVED/REJECTED/PENDING states at a glance in the template list. */
  const getApprovalBadge = (status) => {
    const badges = {
      APPROVED: 'bg-success',
      REJECTED: 'bg-danger',
      PENDING: 'bg-warning text-dark',
      NOT_REQUIRED: 'bg-info',
    };
    return badges[status] || 'bg-secondary';
  };

  /* WHAT: Maps a database technology name to a Bootstrap badge color class.
   * WHY: Consistent color per technology (red=Oracle, blue=MSSQL, etc.) matches
   *      the color scheme used throughout the DAMA application. */
  const getTechnologyBadge = (tech) => {
    const badges = {
      ORACLE: 'bg-danger',
      MSSQL: 'bg-primary',
      POSTGRESQL: 'bg-info',
      MYSQL: 'bg-warning text-dark',
      CASSANDRA: 'bg-success',
    };
    return badges[tech] || 'bg-secondary';
  };

  return (
    <div>
      {/* Header with filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row align-items-end">
            <div className="col-md-2">
              <label className="form-label">Technology</label>
              <select
                className="form-select"
                value={filters.technology}
                onChange={(e) => setFilters({ ...filters, technology: e.target.value })}
              >
                <option value="">All Technologies</option>
                {technologies.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Approval</label>
              <select
                className="form-select"
                value={filters.approval}
                onChange={(e) => setFilters({ ...filters, approval: e.target.value })}
              >
                <option value="">All</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
              </select>
            </div>
            <div className="col-md-6 text-end">
              <button className="btn btn-primary" onClick={handleCreateNew}>
                <i className="fas fa-plus me-2"></i>
                New Template
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      {/* Templates Table */}
      <div className="card">
        <div className="card-header bg-secondary text-white">
          <h5 className="mb-0">
            <i className="fas fa-file-code me-2"></i>
            SQL Templates ({templates.length})
          </h5>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : templates.length > 0 ? (
            <table className="table table-hover mb-0">
              <thead className="table-dark">
                <tr>
                  <th>Template</th>
                  <th>Technology</th>
                  <th className="text-center">Validation</th>
                  <th className="text-center">Approval</th>
                  <th className="text-center">Version</th>
                  <th>Created</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((template) => (
                  <tr key={template.template_id}>
                    <td>
                      <strong>{template.template_code}</strong>
                      <br />
                      <small className="text-muted">{template.template_name}</small>
                    </td>
                    <td>
                      <span className={`badge ${getTechnologyBadge(template.technology)}`}>
                        {template.technology}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className={`badge ${getStatusBadge(template.validation_status)}`}>
                        {template.validation_status}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className={`badge ${getApprovalBadge(template.approval_status)}`}>
                        {template.approval_status}
                      </span>
                    </td>
                    <td className="text-center">v{template.version}</td>
                    <td>
                      <small>
                        {template.created_at
                          ? new Date(template.created_at).toLocaleDateString()
                          : '-'}
                      </small>
                      <br />
                      <small className="text-muted">{template.created_by}</small>
                    </td>
                    <td className="text-center">
                      <div className="btn-group btn-group-sm">
                        <button
                          className="btn btn-outline-primary"
                          onClick={() => handleView(template)}
                          title="View"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() => handleEdit(template)}
                          title="Edit"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        {template.approval_status === 'PENDING' && (
                          <>
                            <button
                              className="btn btn-outline-success"
                              onClick={() => handleApprove(template.template_id, 'approve')}
                              title="Approve"
                            >
                              <i className="fas fa-check"></i>
                            </button>
                            <button
                              className="btn btn-outline-danger"
                              onClick={() => handleApprove(template.template_id, 'reject')}
                              title="Reject"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </>
                        )}
                        <button
                          className="btn btn-outline-danger"
                          onClick={() => handleDelete(template.template_id)}
                          title="Delete"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-5 text-muted">
              <i className="fas fa-file-code fa-3x mb-3 opacity-25"></i>
              <p>No templates found</p>
              <button className="btn btn-primary" onClick={handleCreateNew}>
                <i className="fas fa-plus me-2"></i>
                Create Your First Template
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Template Modal */}
      {showModal && (
        <div
          className="modal fade show"
          style={{
            display: 'block',
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1050,
            overflow: 'auto',
          }}
        >
          <div style={{ padding: '20px' }}>
            <div
              className="modal-content"
              style={{ maxWidth: '1200px', width: '100%', margin: '0 auto' }}
            >
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  <i className={`fas ${modalMode === 'create' ? 'fa-plus' : modalMode === 'edit' ? 'fa-edit' : 'fa-eye'} me-2`}></i>
                  {modalMode === 'create' ? 'New SQL Template' : modalMode === 'edit' ? 'Edit Template' : 'View Template'}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <div className="modal-body" style={{ minHeight: '550px' }}>
                <div className="row" style={{ height: '100%' }}>
                  {/* Left column - Basic info */}
                  <div className="col-md-4" style={{ maxHeight: '550px', overflowY: 'auto' }}>
                    <div className="mb-3">
                      <label className="form-label">Template Code *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.template_code}
                        onChange={(e) => setFormData({ ...formData, template_code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') })}
                        disabled={modalMode === 'view' || modalMode === 'edit'}
                        placeholder="e.g., ORACLE_DBA_USERS"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Template Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.template_name}
                        onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                        disabled={modalMode === 'view'}
                        placeholder="Human-readable name"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Technology *</label>
                      <select
                        className="form-select"
                        value={formData.technology}
                        onChange={(e) => setFormData({ ...formData, technology: e.target.value })}
                        disabled={modalMode === 'view'}
                      >
                        {technologies.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Output Type</label>
                      <select
                        className="form-select"
                        value={formData.output_type}
                        onChange={(e) => setFormData({ ...formData, output_type: e.target.value })}
                        disabled={modalMode === 'view'}
                      >
                        {outputTypes.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        disabled={modalMode === 'view'}
                        placeholder="What does this template do?"
                      ></textarea>
                    </div>
                    <div className="form-check mb-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="approvalRequired"
                        checked={formData.approval_required}
                        onChange={(e) => setFormData({ ...formData, approval_required: e.target.checked })}
                        disabled={modalMode === 'view'}
                      />
                      <label className="form-check-label" htmlFor="approvalRequired">
                        Require approval before execution
                      </label>
                    </div>

                    {/* Validation Result */}
                    {validationResult && (
                      <div className={`alert ${validationResult.is_valid ? 'alert-success' : 'alert-danger'}`}>
                        <strong>
                          {validationResult.is_valid ? (
                            <><i className="fas fa-check-circle me-2"></i>Valid</>
                          ) : (
                            <><i className="fas fa-times-circle me-2"></i>Invalid</>
                          )}
                        </strong>
                        {validationResult.violations?.length > 0 && (
                          <ul className="mb-0 mt-2">
                            {validationResult.violations.map((v, i) => (
                              <li key={i}><small>{v.rule}: {v.description}</small></li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Right column - SQL Editor */}
                  <div className="col-md-8">
                    <div className="mb-3" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <label className="form-label">
                        SQL Statement *
                        <small className="text-muted ms-2">
                          Use {'{{placeholder_name}}'} for dynamic values
                        </small>
                      </label>
                      <textarea
                        className="form-control font-monospace"
                        value={formData.sql_statement}
                        onChange={(e) => setFormData({ ...formData, sql_statement: e.target.value })}
                        disabled={modalMode === 'view'}
                        placeholder={`-- Example ${formData.technology} Query
SELECT
    username,
    account_status,
    created
FROM dba_users
WHERE username NOT IN ('SYS', 'SYSTEM')
ORDER BY username`}
                        style={{
                          fontSize: '13px',
                          lineHeight: '1.5',
                          minHeight: '400px',
                          height: 'calc(100% - 30px)',
                          resize: 'vertical',
                          fontFamily: 'Consolas, Monaco, "Courier New", monospace'
                        }}
                      ></textarea>
                    </div>

                    {/* Security notice */}
                    <div className="alert alert-info mb-0">
                      <i className="fas fa-shield-alt me-2"></i>
                      <strong>Security:</strong> INSERT, UPDATE, DELETE, DROP, and other DDL/DML statements are automatically blocked.
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  {modalMode === 'view' ? 'Close' : 'Cancel'}
                </button>
                {modalMode !== 'view' && (
                  <>
                    {selectedTemplate && (
                      <button
                        type="button"
                        className="btn btn-outline-info"
                        onClick={handleValidate}
                      >
                        <i className="fas fa-check-double me-2"></i>
                        Validate
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSave}
                      disabled={saving || !formData.template_code || !formData.template_name || !formData.sql_statement}
                    >
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Save Template
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SqlTemplates;
